import React from 'react';
import {
  Box,
  Text,
  useColorModeValue,
  SimpleGrid,
  VStack,
  HStack,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { getVehicleBrand } from '../../../utils/vehicleBrand';
import { 
  FaCar, 
  FaIdCard, 
  FaCogs, 
  FaCalendarAlt, 
  FaPalette, 
  FaGasPump, 
  FaTachometerAlt, 
  FaUser, 
  FaPhone, 
  FaUserTie 
} from 'react-icons/fa';

const VehicleInfo = ({ vehicleData }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const iconColor = useColorModeValue('blue.500', 'blue.300');

  const InfoField = ({ label, value, icon }) => (
    <Box 
      p={3} 
      bg={sectionBg} 
      borderRadius="md" 
      border="1px solid" 
      borderColor={borderColor}
    >
      <HStack spacing={2} align="start">
        {icon && (
          <Icon 
            as={icon} 
            color={iconColor} 
            boxSize={4}
            mt={1}
          />
        )}
        <VStack align="start" spacing={1} flex={1}>
          <Text 
            fontSize="xs" 
            fontWeight="semibold" 
            color={labelColor} 
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {label}
          </Text>
          <Text 
            fontSize="md" 
            color={valueColor} 
            fontWeight="medium"
          >
            {value || 'N/A'}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );

  const SectionTitle = ({ title }) => (
    <HStack mb={4} align="center">
      <Text 
        fontSize="lg" 
        fontWeight="bold" 
        color={valueColor}
      >
        {title}
      </Text>
      <Divider />
    </HStack>
  );

  return (
    <Box mt={4} p={4}>
      {/* Información Principal del Vehículo */}
      <SectionTitle title="Información del Vehículo" />
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} mb={6}>
        <InfoField 
          label="Matrícula" 
          value={vehicleData?.plate} 
          icon={FaIdCard}
        />
        <InfoField 
          label="Chasis" 
          value={vehicleData?.chassisNumber} 
          icon={FaCogs}
        />
        <InfoField 
          label="Motor" 
          value={vehicleData?.engineNumber} 
          icon={FaCogs}
        />
        <InfoField 
          label="Marca" 
          value={getVehicleBrand(vehicleData)} 
          icon={FaCar}
        />
        <InfoField 
          label="Modelo" 
          value={vehicleData?.model?.description || vehicleData?.model} 
          icon={FaCar}
        />
        <InfoField 
          label="Año" 
          value={vehicleData?.year} 
          icon={FaCalendarAlt}
        />
        <InfoField 
          label="Color" 
          value={vehicleData?.color?.name || vehicleData?.color} 
          icon={FaPalette}
        />
        <InfoField 
          label="Tipo de Combustible" 
          value={vehicleData?.fuelType?.type || vehicleData?.fuelType} 
          icon={FaGasPump}
        />
        <InfoField 
          label="Kilómetros" 
          value={vehicleData?.kilometers ? `${vehicleData.kilometers.toLocaleString()} km` : 'N/A'} 
          icon={FaTachometerAlt}
        />
      </SimpleGrid>

      {/* Información de Representante */}
      {(vehicleData?.representative || vehicleData?.representativeContact) && (
        <>
          <SectionTitle title="Representante" />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={6}>
            <InfoField 
              label="Representante" 
              value={vehicleData?.representative} 
              icon={FaUserTie}
            />
            <InfoField 
              label="Contacto" 
              value={vehicleData?.representativeContact} 
              icon={FaPhone}
            />
          </SimpleGrid>
        </>
      )}

      {/* Información Adicional */}
      <SectionTitle title="Información Adicional" />
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
        <InfoField 
          label="Dueño Actual" 
          value={vehicleData?.currentCustomer?.name} 
          icon={FaUser}
        />
        <InfoField 
          label="Documento" 
          value={
            vehicleData?.currentCustomer?.document ||
            vehicleData?.currentCustomer?.documentNumber
          } 
          icon={FaIdCard}
        />
        <InfoField 
          label="Teléfono" 
          value={
            vehicleData?.currentCustomer?.mobilePhone ||
            vehicleData?.currentCustomer?.phone
          } 
          icon={FaPhone}
        />
        <InfoField 
          label="Fecha de Creación" 
          value={vehicleData?.createdAt ? 
            new Date(vehicleData.createdAt).toLocaleDateString('es-ES') : 'N/A'} 
          icon={FaCalendarAlt}
        />
        <InfoField 
          label="Última Actualización" 
          value={vehicleData?.updatedAt ? 
            new Date(vehicleData.updatedAt).toLocaleDateString('es-ES') : 'N/A'} 
          icon={FaCalendarAlt}
        />
      </SimpleGrid>
    </Box>
  );
};

export default VehicleInfo;